import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, MenuItems } from '@headlessui/react';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  formatDateToLocal,
  formatSchedule,
  getDateTimePatterns,
} from 'utils/date';
import {
  formatReminderActiveWindow,
} from 'utils/reminders';
import { COLOR_MAP_BY_STATUS, NEUTRAL_CLASSNAMES } from 'constants/colors';
import { REMINDER_STATUSES } from 'constants/reminders';
import StackedListMenuItem from '../StackedListMenuItem/StackedListMenuItem';
import StackedListMenuToggle from '../StackedListMenuToggle/StackedListMenuToggle';
import { Badge } from 'components/badges';

export default function StackedListItem({ item, settings, onAction, isUpdating = false }) {
  const statusColor = COLOR_MAP_BY_STATUS[item.status] || NEUTRAL_CLASSNAMES;
  const scheduleText = formatSchedule(item, settings, true);
  const activeWindowText = formatReminderActiveWindow(item, settings);
  const { dateTimePattern } = getDateTimePatterns(settings);

  const createdDate = formatDateToLocal(item.createdAt, dateTimePattern);
  const isPaused = item.status === REMINDER_STATUSES.PAUSED;
  const canToggleStatus =
    item.status === REMINDER_STATUSES.ACTIVE || item.status === REMINDER_STATUSES.PAUSED;
  const ToggleIcon = isPaused ? PlayIcon : PauseIcon;
  const toggleLabel = isPaused ? 'Activate reminder' : 'Pause reminder';

  const handleAction = (action) => {
    if (onAction) {
      onAction(action, item);
    }
  };

  return (
    <li className="flex justify-between gap-x-6 py-5 px-4 hover:bg-gray-800/50 transition-colors">
      <div className="flex min-w-0 gap-x-4">
        <div className="min-w-0 flex-auto">
          <div className="flex flex-col items-start gap-y-1 sm:flex-row sm:items-center sm:gap-x-3">
            <p className="text-sm font-semibold leading-6 text-white">{item.subject}</p>
            <Badge size="sm" className={statusColor}>
              {item.status}
            </Badge>
          </div>
          {item.worker && (
            <div className="mt-1 flex items-center gap-x-1.5 text-xs leading-5 text-gray-400">
              <span>Worker:</span>
              <Link
                to={`/workers/view?id=${item.worker._id}`}
                state={{ from: '/reminders' }}
                className="truncate font-medium text-indigo-400 hover:text-indigo-300 hover:underline"
              >
                {item.worker.name || 'Worker assigned'}
              </Link>
              <button
                type="button"
                onClick={() => handleAction('remove-worker')}
                aria-label="Remove worker"
                className="inline-flex size-5 cursor-pointer items-center justify-center rounded-full text-gray-400 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <XMarkIcon className="size-4" aria-hidden="true" />
              </button>
            </div>
          )}
          {item.todo && (
            <div className="mt-1 flex items-center gap-x-1.5 text-xs leading-5 text-gray-400">
              <span>Todo:</span>
              <Link
                to={`/todos/edit?id=${item.todo._id}`}
                state={{ from: '/reminders' }}
                className="truncate font-medium text-indigo-400 hover:text-indigo-300 hover:underline"
              >
                {item.todo.name || 'Todo assigned'}
              </Link>
              <button
                type="button"
                onClick={() => handleAction('remove-todo')}
                aria-label="Remove todo"
                className="inline-flex size-5 cursor-pointer items-center justify-center rounded-full text-gray-400 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <XMarkIcon className="size-4" aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="mt-1 space-y-1 text-xs leading-5 text-gray-400">
            <div className="flex flex-col items-start gap-y-1 lg:flex-row lg:items-center lg:gap-x-2">
              <p className="truncate">{scheduleText}</p>
              <svg viewBox="0 0 2 2" className="hidden lg:block h-0.5 w-0.5 fill-current">
                <circle cx={1} cy={1} r={1} />
              </svg>
              <p className="whitespace-nowrap">
                Created on <time dateTime={item.createdAt}>{createdDate}</time>
              </p>
            </div>
            {activeWindowText && <p className="truncate">{activeWindowText}</p>}
          </div>
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end justify-center">
        <div className="flex items-center gap-x-4">
          {canToggleStatus && (
            <button
              type="button"
              onClick={() => handleAction('toggle-status')}
              aria-label={toggleLabel}
              title={toggleLabel}
              disabled={isUpdating}
              className="hidden lg:inline-flex items-center justify-center rounded-md bg-white/10 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ToggleIcon className="size-5" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => handleAction('view')}
            className="hidden lg:block rounded-md bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20 cursor-pointer"
          >
            View reminder
          </button>
          <Menu as="div" className="relative flex-none">
            <StackedListMenuToggle />
            <MenuItems
              transition
              anchor="bottom end"
              className="w-32 origin-top-right rounded-md bg-gray-800 py-2 shadow-lg ring-1 ring-white/10 focus:outline-none transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="lg:hidden">
                <StackedListMenuItem onClick={() => handleAction('view')}>
                  View reminder
                </StackedListMenuItem>
                {canToggleStatus && (
                  <StackedListMenuItem onClick={() => handleAction('toggle-status')} disabled={isUpdating}>
                    {isPaused ? 'Activate' : 'Pause'}
                  </StackedListMenuItem>
                )}
              </div>
              <StackedListMenuItem onClick={() => handleAction('logs')}>
                Logs
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('edit')}>
                Edit
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('clone')}>
                Clone
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('add-worker')}>
                Add worker
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('add-todo')}>
                Add todo
              </StackedListMenuItem>
              <StackedListMenuItem onClick={() => handleAction('add-instruction')}>
                Add instruction
              </StackedListMenuItem>
              <StackedListMenuItem variant="danger" onClick={() => handleAction('delete')}>
                Delete
              </StackedListMenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </li>
  );
}
